import localConfig from 'mode-shared/configs/config';
import { QueryKeys } from 'mode-shared/constants/queryKeys';
import { contentfulClient } from 'mode-shared/utils';
import { useQuery } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { StreamersVideosSkeleton } from '../types';

export type StreamerProvider = 'youtube' | 'twitch' | 'twitter' | null;

const getVideoProvider = (url?: string): StreamerProvider => {
  if (!url) return null;

  if (url.includes('youtube.com') || url.includes('youtu.be')) {
    return 'youtube';
  }

  if (url.includes('twitch.tv')) {
    return 'twitch';
  }

  if (url.includes('x.com')) {
    return 'twitter';
  }

  return null;
};

const getEmbedUrl = (url?: string, isPrime?: boolean) => {
  if (!url) return '';

  const provider = getVideoProvider(url);

  if (provider === 'youtube') {
    const videoId =
      url.split('watch?v=')[1]?.split('&')[0] ||
      url.split('youtu.be/')[1]?.split('?')[0] ||
      url.split('/live/')[1]?.split(/[?&]/)[0] ||
      '';
    return videoId
      ? `https://www.youtube.com/embed/${videoId}${isPrime ? '?autoplay=1&modestbranding=1&rel=0' : '?autoplay=1&mute=1&modestbranding=1&rel=0'}`
      : '';
  }

  if (provider === 'twitch') {
    if (url.includes('/videos/')) {
      const videoId = url.split('/videos/')[1]?.split('?')[0] || '';
      return videoId
        ? `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}${isPrime ? '' : '&muted=true'}`
        : '';
    }
    const channelName = url.split('twitch.tv/')[1]?.split('?')[0] || '';
    return channelName
      ? `https://player.twitch.tv/?channel=${channelName}&parent=${window.location.hostname}${isPrime ? '' : '&muted=true'}`
      : '';
  }

  if (provider === 'twitter') {
    return url.replace('x.com', 'twitter.com');
  }

  return '';
};

const getChannelUrl = (url?: string, channelName?: string) => {
  if (!url || !channelName) return '';

  const provider = getVideoProvider(url);

  if (provider === 'youtube') {
    return `https://www.youtube.com/@${channelName}`;
  }

  if (provider === 'twitch') {
    return `https://www.twitch.tv/${channelName}`;
  }

  if (provider === 'twitter') {
    return `https://x.com/${channelName}`;
  }

  return url;
};

const fetchStreamersVideos = async () => {
  try {
    let content = [];
    if (localConfig.showContentLive) {
      content = (
        await contentfulClient.getEntries<StreamersVideosSkeleton>({
          content_type: 'streamersVideos',
          order: ['-sys.createdAt'],
        })
      ).items;
    } else {
      content = (
        await axios.get<void, AxiosResponse<StreamersVideosSkeleton[]>>(
          `${localConfig.contentfulUrl}/streamersVideos.json`
        )
      ).data;
    }

    content = content.filter((entry) => entry.fields.videoUrl && entry.fields.videoUrl && entry.fields.streamer);

    // Find the latest video's creation date
    const latestDate = content.reduce((latest, entry) => {
      const isEducational = entry.fields.educational;
      // educational videos cannot be prime
      if (isEducational) return latest;
      const createdAt = 'sys' in entry ? new Date(entry.sys.createdAt) : new Date();
      return createdAt > latest ? createdAt : latest;
    }, new Date(0));

    return content.map((entry) => {
      const { videoUrl, streamer, title } = entry.fields;
      const createdAt = 'sys' in entry ? new Date(entry.sys.createdAt) : new Date();
      const isPrime = createdAt.getTime() === latestDate.getTime();

      return {
        rawVideoUrl: videoUrl,
        videoUrl: getEmbedUrl(videoUrl, isPrime),
        channelUrl: getChannelUrl(videoUrl, streamer),
        channelName: streamer,
        streamerName: entry.fields.author?.fields?.name,
        referralCode: entry.fields.author?.fields?.referralCode,
        educational: entry.fields.educational,
        provider: getVideoProvider(videoUrl),
        title: isPrime ? `Live now: ${title}` : title,
        isPrime,
        createdAt,
      };
    });
  } catch (e) {
    console.error(e);
    return;
  }
};

export const useStreamersVideos = (options = {}) => {
  return useQuery({
    queryKey: [QueryKeys.STREAMERS_VIDEOS],
    queryFn: fetchStreamersVideos,
    ...options,
  });
};

export type StreamersVideo = NonNullable<Awaited<ReturnType<typeof fetchStreamersVideos>>>[number];
